/**
 * Records one AI Assistant conversation and persists it to
 * /api/ai/assistant/conversations so it survives the browser.
 *
 * Two rules shape this module:
 *
 *   1. The log NEVER blocks or breaks the chat. Every save is fire-and-forget;
 *      nothing here throws into the popover, and a backend that is down or
 *      unreachable costs the operator nothing but a missing history row.
 *
 *   2. A save is an idempotent upsert of the WHOLE conversation under one
 *      client-generated id. That is what makes retries trivial: a save that
 *      never landed is repaired by the next turn's save, which carries
 *      everything again, so there is no append-log to reconcile and no
 *      ordering to get wrong.
 *
 * The recorder keeps its own model of the conversation rather than reusing the
 * popover's `conversation` state, because that state deliberately holds only
 * what is rendered — the tool calls and mutation verdicts an investigation
 * actually needs never reach it.
 */

// Backoff for a failed save. The last entry is the steady-state retry: a
// conversation that keeps failing keeps trying at that interval for as long as
// the drawer is open, because the operator may well fix the backend mid-chat.
const RETRY_DELAYS_MS = [2000, 8000, 30000];

// The server refuses a save it can never accept (a malformed id, a transcript
// past the size cap). Retrying those forever would be a busy loop against a
// wall, so the recorder stops logging that conversation and the chat carries
// on untouched.
const PERMANENT_STATUSES = new Set([400, 401, 403, 404, 405, 413, 422]);

const MAX_SUMMARY_CHARS = 300;

export function newConversationId() {
  return `gw${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function truncate(value, limit = MAX_SUMMARY_CHARS) {
  const text = typeof value === "string" ? value : "";
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

/**
 * Condense a tool result into one line. The raw payload is not logged: a
 * `list_db_columns` result is thousands of rows, and what an investigation
 * needs is that the call ran and roughly what came back.
 */
export function summarizeToolResult(result) {
  if (!result || typeof result !== "object") {
    return { ok: undefined, result: "", error: "" };
  }
  if (result.ok === false) {
    const error = result.error && typeof result.error === "object" ? result.error : {};
    const code = typeof error.code === "string" && error.code ? error.code : "ERROR";
    const message = typeof error.message === "string" ? error.message : "";
    return { ok: false, result: "", error: truncate(message ? `${code}: ${message}` : code) };
  }
  return { ok: true, result: summarizeData(result.data), error: "" };
}

function summarizeData(data) {
  if (data === undefined || data === null) return "ok";
  if (typeof data === "string") return truncate(data);
  if (Array.isArray(data)) return `${data.length} item(s)`;
  try {
    return truncate(JSON.stringify(data));
  } catch {
    return "ok";
  }
}

/**
 * Build the confirmation entry for an approval card the assistant raised.
 * Verdict starts as "pending" — the turn ends with the card still on screen,
 * and the next save overwrites it once the operator clicks.
 */
export function confirmationFromPending(pending) {
  const proposed = pending?.proposed || {};
  const summary = proposed.summary || {};
  return {
    token: pending?.confirmationToken || "",
    kind: proposed.mutation?.kind || "",
    title: summary.title || "",
    target: summary.target || "",
    verdict: "pending",
    result: "",
    error: "",
  };
}

/**
 * @param {Object} opts
 * @param {(id: string, payload: Object) => Promise<any>} opts.save
 * @param {string} [opts.surface]
 * @param {string} [opts.userName]
 * @param {(fn: Function, delay: number) => any} [opts.setTimer]  injectable for tests
 * @param {(handle: any) => void} [opts.clearTimer]
 * @param {number[]} [opts.retryDelaysMs]
 */
export function createAssistantRecorder({
  save,
  surface = "gateway",
  userName = "",
  setTimer = (fn, delay) => setTimeout(fn, delay),
  clearTimer = (handle) => clearTimeout(handle),
  retryDelaysMs = RETRY_DELAYS_MS,
} = {}) {
  let id = newConversationId();
  let turns = [];
  let provider = "";
  let model = "";
  let lastError = "";
  let disabled = false;

  // Save coalescing: one request in flight at a time. A save requested while
  // one is running sets `pending` instead of racing it — two concurrent full
  // upserts of the same row would only fight over which snapshot lands last.
  let inFlight = false;
  let pending = false;
  let attempt = 0;
  let retryHandle = null;

  const cancelRetry = () => {
    if (retryHandle !== null) {
      clearTimer(retryHandle);
      retryHandle = null;
    }
  };

  const payload = () => ({
    surface,
    userName,
    provider,
    model,
    title: firstUserMessage(turns),
    error: lastError,
    turns: turns.map(serializeTurn),
  });

  const run = async () => {
    if (disabled || inFlight || turns.length === 0) return;
    inFlight = true;
    pending = false;
    const conversationID = id;
    try {
      await save(conversationID, payload());
      attempt = 0;
    } catch (err) {
      // A conversation that was cleared mid-flight must not resurrect its own
      // retry loop against the old id.
      if (conversationID !== id) return;
      const status = err?.status;
      if (PERMANENT_STATUSES.has(status)) {
        disabled = true;
        return;
      }
      const delay = retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)];
      attempt += 1;
      pending = true;
      cancelRetry();
      retryHandle = setTimer(() => {
        retryHandle = null;
        run();
      }, delay);
      return;
    } finally {
      inFlight = false;
    }
    if (pending) run();
  };

  return {
    get conversationId() {
      return id;
    },
    get isDisabled() {
      return disabled;
    },
    // Exposed for tests and for the history viewer; never sent as-is.
    get turns() {
      return turns;
    },

    setModel(nextProvider, nextModel) {
      provider = typeof nextProvider === "string" ? nextProvider : "";
      model = typeof nextModel === "string" ? nextModel : "";
    },

    // "Clear conversation" starts a genuinely new record rather than
    // overwriting the old one — the point of the log is that the previous
    // conversation is still there afterwards.
    reset() {
      cancelRetry();
      id = newConversationId();
      turns = [];
      lastError = "";
      disabled = false;
      pending = false;
      attempt = 0;
    },

    // startTurn opens the user side of a turn. Called before the request goes
    // out, so the record exists from the moment the conversation starts even
    // if the answer never arrives.
    startTurn({ content = "", attachmentNames = [], at = new Date() } = {}) {
      turns.push({
        role: "user",
        content,
        attachmentNames: attachmentNames.filter(Boolean),
        at,
      });
      turns.push({
        role: "assistant",
        content: "",
        toolCalls: [],
        confirmations: [],
        error: "",
        at: null,
      });
    },

    noteToolStart(callID, name, args) {
      const turn = currentAssistantTurn(turns);
      if (!turn) return;
      turn.toolCalls.push({ id: callID, name, args, result: "", error: "", ok: undefined });
    },

    noteToolResult(callID, name, result) {
      const turn = currentAssistantTurn(turns);
      if (!turn) return;
      const summary = summarizeToolResult(result);
      // Match on the call id; fall back to the last unresolved call of the
      // same name so an off-protocol inline call still gets its result.
      const entry =
        turn.toolCalls.find((call) => call.id && call.id === callID) ||
        [...turn.toolCalls].reverse().find((call) => call.name === name && !call.resolved);
      if (!entry) {
        turn.toolCalls.push({ id: callID, name, args: undefined, resolved: true, ...summary });
        return;
      }
      Object.assign(entry, summary, { resolved: true });
    },

    finishTurn({ content = "", error = "", confirmation = null } = {}) {
      const turn = currentAssistantTurn(turns);
      if (!turn) return;
      turn.content = content;
      turn.error = error;
      turn.at = new Date();
      if (confirmation) turn.confirmations.push(confirmation);
      lastError = error || "";
    },

    // The operator's click on an approval card lands after the turn that
    // raised it has already been saved; the next save carries the verdict.
    resolveConfirmation(token, verdict, { result = "", error = "" } = {}) {
      for (let index = turns.length - 1; index >= 0; index -= 1) {
        const entry = (turns[index].confirmations || []).find((c) => c.token === token);
        if (entry) {
          entry.verdict = verdict;
          entry.result = result;
          entry.error = error;
          return true;
        }
      }
      return false;
    },

    // save never rejects: the caller is a chat turn, and a logging failure
    // must not surface as a chat failure.
    save() {
      if (disabled) return;
      if (inFlight) {
        pending = true;
        return;
      }
      cancelRetry();
      run();
    },

    // Stop the retry loop when the popover unmounts.
    dispose() {
      cancelRetry();
      pending = false;
    },
  };
}

function currentAssistantTurn(turns) {
  const last = turns[turns.length - 1];
  return last && last.role === "assistant" ? last : null;
}

function firstUserMessage(turns) {
  const first = turns.find((turn) => turn.role === "user" && turn.content);
  return first ? first.content : "";
}

// serializeTurn drops the recorder's bookkeeping fields (`id`, `resolved`,
// `token`) — the server rejects unknown fields, and they mean nothing outside
// this module.
function serializeTurn(turn) {
  const out = { role: turn.role };
  if (turn.content) out.content = turn.content;
  if (turn.at) out.at = new Date(turn.at).toISOString();
  if (turn.attachmentNames?.length) out.attachmentNames = turn.attachmentNames;
  if (turn.error) out.error = turn.error;
  if (turn.toolCalls?.length) {
    out.toolCalls = turn.toolCalls.map((call) => {
      const entry = { name: call.name };
      if (call.args !== undefined) entry.args = call.args;
      if (call.result) entry.result = call.result;
      if (call.error) entry.error = call.error;
      if (typeof call.ok === "boolean") entry.ok = call.ok;
      return entry;
    });
  }
  if (turn.confirmations?.length) {
    out.confirmations = turn.confirmations.map((confirmation) => {
      const entry = { verdict: confirmation.verdict || "pending" };
      if (confirmation.kind) entry.kind = confirmation.kind;
      if (confirmation.title) entry.title = confirmation.title;
      if (confirmation.target) entry.target = confirmation.target;
      if (confirmation.result) entry.result = confirmation.result;
      if (confirmation.error) entry.error = confirmation.error;
      return entry;
    });
  }
  return out;
}

export default createAssistantRecorder;
